import * as React from 'react';
import { createInternalArticleChange, getInternalArticle, updateInternalArticle } from '../../../../api/articlesPortalClientApi';
import { ProtectedPageWrapper } from '../../../../designSystem/layouts/pageWrappers/ProtectedPageWrapper';
import { SingleColumnLayout } from '../../../../designSystem/layouts/SingleColumnLayout';
import { LoadingPageOverlay } from '../../../../designSystem/LoadingPageOverlay';
import { PageHeader } from '../../../../designSystem/PageHeader';
import { Flex } from '../../../../designSystem/compositeComponents/FlexHelpers';
import { useParams } from 'react-router-dom';
import { Button } from '../../../../designSystem/button/Button';
import { ToolBarContainer } from '../../../../designSystem/compositeComponents/ButtonToolBar';
import { Card } from '../../../../designSystem/Card';
import { CommandResult, fieldValidation, InternalArticleFieldChange, InternalSubscriberArticleChangeView, InternalSubscriberArticleView, rng } from '../../../../amorg-common';
import { EditableText } from '../../../../designSystem/compositeComponents/EditableText';
import { toDateString, toDateTimeString } from '../../../../utils/date';
import { H2, H3 } from '../../../../designSystem/typography/Heading';
import { FormField } from '../../../../designSystem/formField/FormField';
import { UserSelectorAvatarButton } from '../../../../designSystem/compositeComponents/itemSelector/UserSelectorAvatarButton';
import { P, Small } from '../../../../designSystem/typography/Body';
import { Label } from '../../../../designSystem/Label';
import { DateInput } from '../../../../designSystem/inputs/DateInput';
import { ArticleImportanceSelector } from '../../_components/ArticleImportanceSelector';
import { ImportanceAvatar } from '../../../../designSystem/avatar/ImportanceAvatar';
import { ConfirmationButton } from '../../../../designSystem/compositeComponents/ConfirmationButton';
import { Divider } from '../../../../designSystem/Divider';
import { HtmlEditorWithAutoSave } from '../../../../designSystem/inputs/HtmlEditorWithAutoSave';
import { HtmlEditorViewer } from '../../../../designSystem/inputs/HtmlEditorViewer';
import { FileLinkCommentPanel } from '../../../../designSystem/compositeComponents/FileLinkCommentPanel';

export function InternalArticlePage (): JSX.Element
{

    return (
        <ProtectedPageWrapper requiredPermissions={['ViewCustomFormAnswerList']} selectedMenuItemKey={'articles'}>
            <PrivateInternalArticlePage />
        </ProtectedPageWrapper>
    )

}

 function PrivateInternalArticlePage (): JSX.Element
{
    const { internalArticleId } = useParams<{ internalArticleId: string; }>();

    const [loading, setLoading] = React.useState(true);
    const [article, setArticle] = React.useState<InternalSubscriberArticleView | null>(null);

    React.useEffect(() =>
    {
        const getData = async () =>
        {
            setLoading(true);
            const art = await getInternalArticle(internalArticleId);
            setArticle(art);
            setLoading(false);
        };
        getData();
    }, [internalArticleId]);

    async function handleCreateNewVersion ()
    {
        setLoading(true);
        const result = await createInternalArticleChange(internalArticleId || '', rng.getRandomId());
        if (result.isSuccess)
        {
            const art = await getInternalArticle(internalArticleId);
            setArticle(art);
        }
        setLoading(false);
    }

    async function handleChange (change: InternalArticleFieldChange) : Promise<CommandResult>
    {
        setLoading(true);
        const result = await updateInternalArticle(internalArticleId || '', change);
        if (result.isSuccess)
        {
            const art = await getInternalArticle(internalArticleId);
            setArticle(art);
        }
        setLoading(false);
        return result;
    }

    return (
        <SingleColumnLayout
            noDivider
            contentContainer={
                <Flex flexDirection='column' gap={32}>

                    <LoadingPageOverlay loading={loading} />

                    <PageHeader
                        headerStyle="secondary-gray"
                        headerIcon='gavel'
                        headerText='Lovservice'
                        buttons={
                            <ToolBarContainer>
                                <Button variant="text" icon={'help'} toolTipText='Du kan bruge andre forpligtelser og krav til at dokumentere krav og forpligtelser, som ikke er lovkrav. Eksempelvis interne politikker og procedurer.' />
                            </ToolBarContainer>
                        }
                        breadCrumbs={[
                            { text: 'Tilbage til oversigten', url: '/articles/internal', leadingIcon: 'arrow_back' },
                        ]}
                    />

                    {article &&
                        <>
                            <ArticleCard article={article} onChange={handleChange} />

                            <ConfirmationButton
                                modalButtonVariant='filled'
                                requireKey={false}
                                title="Opret ny version"
                                content={
                                    <P>Er du sikker på, at du vil oprette en ny version af denne artikel? Den nuværende version vil blive bevaret som en tidligere version, men vil ikke længere kunne redigeres.</P>
                                }
                                buttonText="Opret ny version"
                                onConfirm={handleCreateNewVersion}
                            />

                            {article.previousVersions.length > 0 &&
                                <Flex flexDirection='column' gap={16}>
                                    <H2>Tidligere versioner</H2>

                                    {article.previousVersions.map(change =>
                                        <PreviousVersionCard key={change.id} change={change} />
                                    )}
                                </Flex>
                            }
                        </>
                    }

                </Flex>
            } />
    );
}

type ArticleCardProps = {
    article: InternalSubscriberArticleView,
    onChange: (change: InternalArticleFieldChange) => Promise<CommandResult>;
};
function ArticleCard ({ article, onChange }: ArticleCardProps): JSX.Element
{
    const [descriptionError, setDescriptionError] = React.useState<string>('');
    const [changeDescriptionError, setChangeDescriptionError] = React.useState<string>('');    
    const [evaluationError, setEvaluationError] = React.useState<string>('');

    async function handleDescriptionSave (value: string):Promise<boolean>
    {
        let error = '';
        if(!fieldValidation.internalArticle.description(value))
        {
            error = 'Skal udfyldes. Maks 5000 tegn.';
        }
        else
        {
            error = (await onChange({ field: 'description', value })).isSuccess
                ? ''
                : 'Der skete en ukendt fejl.';
        }
        setDescriptionError(error);
        return error === '';
    }

    async function handleChangeDescriptionSave (value: string):Promise<boolean>
    {
        let error = ''
        if(!fieldValidation.internalArticleChange.changeDescription(value))
        {
            error = 'Maks 5000 tegn.';
        }
        else
        {
            error = (await onChange({ field: 'changeDescription', value })).isSuccess
                ? ''
                : 'Der skete en ukendt fejl.';
        }
        setChangeDescriptionError(error);
        return error === '';
    }

    async function handleEvaluationSave (value: string):Promise<boolean>
    {
        let error = '';
        if(!fieldValidation.internalArticleChange.evaluationText(value))
        {
            error = 'Skal udfyldes. Maks 5000 tegn.';
        }
        else
        {
            error = (await onChange({ field: 'evaluationText', value })).isSuccess
                ? ''
                : 'Der skete en ukendt fejl.';
        }
        setEvaluationError(error);
        return error === '';
    }

    return (
        <Card variant="outlined">
            <Flex flexDirection='column' gap={32}>
                <Flex flexDirection='column' gap={16}>
                    <EditableText
                        leadingIcon='stacks'
                        styleMode='H2'
                        value={article?.title || ''}
                        ariaLabel='Titel på anden forpligtelse eller krav'
                        placeholder='Titel...'
                        validator={(value) => fieldValidation.internalArticle.title(value)
                            ? { valid: true }
                            : { valid: false, errorMessage: 'Skal udfyldes. Maks 400 tegn.' }
                        }
                        onSave={(value) => onChange({ field: 'title', value })}
                    />

                    <Flex flexDirection='column' gap={2}>
                        <Label>Beskrivelse</Label>
                        <HtmlEditorWithAutoSave
                            id='descriptionEditor'
                            value={article?.description || ''}
                            ariaLabel='Beskrivelse af forpligtelsen eller kravet'
                            placeholder='Beskrivelse af forpligtelsen eller kravet...'
                            errorMessage={descriptionError}
                            onCancel={() => setDescriptionError('')}
                            onSave={handleDescriptionSave}
                        />
                    </Flex>

                    <Flex gap={64}>
                        <FormField
                            label='Liste'
                            inputComponent={<span>Andre forpligtelser og krav</span>}
                        />

                        <FormField
                            label='Ansvarlig'
                            inputComponent={
                                <UserSelectorAvatarButton
                                    selectedUserId={article.ownerUserId || ''}
                                    userFilter={u => u.enabled}
                                    onUserChange={user => onChange({ field: 'ownerUserId', value: user?.id || '' })}
                                />
                            }
                        />
                    </Flex>

                    <Flex justifyContent='flex-end'>
                        <Small>Sidst redigeret: {toDateTimeString(article.lastEdited.date)} af {article?.lastEdited.displayName}</Small>
                    </Flex>
                </Flex>

                <Divider />

                <Flex flexDirection='column' gap={16}>

                    <Flex justifyContent='space-between'>
                        <H3>Version {article.version}</H3>
                    </Flex>
                    {article.version > 1 &&
                        <Flex flexDirection='column'>
                            <Label>Ændringsbeskrivelse</Label>
                            <HtmlEditorWithAutoSave
                                id='changeDescriptionEditor'
                                value={article.changeDescription || ''}
                                ariaLabel='Beskrivelse af seneste ændringer'
                                placeholder='Beskrivelse af seneste ændringer'
                                errorMessage={changeDescriptionError}
                                onCancel={() => setChangeDescriptionError('')}
                                onSave={handleChangeDescriptionSave}
                            />
                        </Flex>
                    }

                    <Flex justifyContent='space-between' alignItems='flex-end'>
                        <FormField
                            label='Dato'
                            inputComponent={
                                <DateInput
                                    value={article.date || null}
                                    onValueChange={(date) => onChange({ field: 'date', value: date })}
                                />
                            }
                        />
                                          
                    </Flex>

                    <Flex flexDirection='column' gap={2}>
                        <Flex justifyContent='space-between' alignItems='flex-end'>
                            <Label>Evaluering</Label>
                            <ArticleImportanceSelector
                                mode='internal-evaluation'
                                selectedImportance={article.evaluation.importance || null}
                                onChangeImportance={(importance) => onChange({ field: 'evaluationImportance', value: importance })}
                            />  
                        </Flex>
                        <HtmlEditorWithAutoSave
                            id='evaluationTextEditor'
                            value={article.evaluation.text || ''}
                            ariaLabel='Evaluering af forpligtelsen eller kravet'
                            placeholder='Evaluering af forpligtelsen eller kravet...'
                            errorMessage={evaluationError}
                            onCancel={() => setEvaluationError('')}
                            onSave={handleEvaluationSave}
                        />
                    </Flex>

                    <Flex justifyContent='flex-end'>
                        <Small>Sidst redigeret: {toDateTimeString(article.lastVersionEdited.date)} af {article?.lastVersionEdited.displayName}</Small>
                    </Flex>

                    <FileLinkCommentPanel
                        fileReference={{ type:'InternalArticle', id:article.id }}
                        linkReference={{ type:'InternalArticle', id:article.id}}
                        commentReference={{ type:'InternalArticle', id:article.id }}
                    />

                </Flex>
            </Flex>


        </Card>
    );
}

type PreviousVersionCardProps = {
    change: InternalSubscriberArticleChangeView;
};
function PreviousVersionCard (props: PreviousVersionCardProps): JSX.Element
{
    return (
        <Card variant="outlined">
            <Flex flexDirection='column' gap={16}>
                <Flex alignItems='center' gap={8}>
                    <ImportanceAvatar importance={props.change.evaluation.importance || undefined} mode='internal-evaluation' />
                    <H3>Version {props.change.version}</H3>
                </Flex>

                <Flex flexDirection='column'>
                    <Label>Ændringsbeskrivelse</Label>
                    <HtmlEditorViewer small content={props.change.changeDescription} />
                </Flex>

                <Flex flexDirection='column'>
                    <Label>Dato</Label>
                    <Small>{props.change.date ? toDateString(props.change.date) : '-'}</Small>
                </Flex>

                <Flex flexDirection='column'>
                    <Label>Evaluering</Label>
                    <HtmlEditorViewer small content={props.change.evaluation.text} />
                </Flex>

                <Small>Sidst redigeret: {toDateString(props.change.lastEdited.date)} af {props.change.lastEdited.displayName}</Small>
            </Flex>
        </Card>
    );
}